<template>
  <b-container fluid class="d-flex flex-column py-3">
    <b-row class="mb-3">
      <b-col v-if="visibleSelectRule">
        <b-form-select
          v-model="item.readId"
          ref="selectReadId"
          :options="rules"
          @change="onChangeReadId"
        ></b-form-select>
      </b-col>
      <b-col v-else>{{ compareNm }}</b-col>
    </b-row>
    <template v-if="visibleCompare">
      <b-form-row class="mb-2">
        <b-col
          cols="4"
          class="text-center text-truncate"
          v-for="(headerField, index) in headerFields"
          :key="headerField.key"
        >
          <label class="d-block bg-secondary text-light rounded-pill small mb-1">{{
            headerField.label
          }}</label>
          <span>{{ item[`headerFormatValueNo${index + 1}`] }}</span>
        </b-col>
        <b-col cols="4" class="text-center text-truncate ml-auto">
          <label class="d-block bg-secondary text-light rounded-pill small mb-1">予定/実績</label>
          <span>{{ item.orderCount }}&nbsp;/&nbsp;{{ item.resultCount }}</span>
        </b-col>
      </b-form-row>
      <b-table
        ref="selectableTable"
        no-border-collapse
        select-mode="single"
        sticky-header
        thead-tr-class="small"
        tbody-tr-class="h5"
        :items="item.details"
        :fields="detailFields"
      ></b-table>
    </template>
  </b-container>
</template>

<script>
import { CompareValueMixin } from '@/mixins/CompareValueMixin';
import { getCompareOrderAPI, putCompareOrderCompleteAPI } from '@/api';
import {
  createReadSelectItems,
  getCompareHeaderFields,
  getCompareDetailFields,
  convertItemFormat
} from '@/compare';
import { allowDup, completeSts, dataLayout, dataType, flg, inputMode, viewSts } from '@/constants';

export default {
  name: 'CompareResult',
  data() {
    return {
      rules: [], // 読取ルール配列
      compareNm: null, // 読取ルール名
      headerFields: [], // ヘッダ項目リスト
      detailFields: [], // 明細項目リスト
      detailIndex: 0, // 照合中の明細インデックス
      selectedInputMode: null, // 選択中の入力モード
      nowCompareNo: 1 // 現在の照合NO
    };
  },
  async mounted() {
    // 画面を初期化
    this.$emit('initView', {
      viewId: this.$options.name, // 画面ID
      useToolButtons: true, // ツールボタン使用
      defaultInputMode: inputMode.BARCODE, // デフォルトバーコード
      usableInputModeBits: inputMode.BARCODE + inputMode.OCR // バーコード＋OCR
    });

    // 読取ルールリスト作成
    const reads = this.$store.getters.reads.filter(
      (read) => read.dataType === dataType.COMPARE_RESULT
    );
    this.rules = createReadSelectItems(reads);

    // 前画面からのパラメータをセット
    const read = this.$store.getters.currentRead;
    const compare = this.$store.getters.currentCompare;

    this.$emit('enabledScan', false);

    // 読取をパラメータ取得した場合は読取ルールを選択状態にする
    if (read) {
      // 読取ルール名取得
      this.compareNm = read.readNm;

      // 読取ルールを選択状態にする
      this.onChangeReadId(read.readId);

      // 照合予定NOをパラメータ取得した場合は照合予定情報を取得する
      const compareOrderNo = this.$route.params['compareOrderNo'];
      if (compareOrderNo) await this.getCompareOrder(compare, compareOrderNo);
    }
  },
  computed: {
    /**
     * 読取ルール選択表示状態
     */
    visibleSelectRule: function () {
      return !this.$route.params['compareOrderNo'];
    },
    /**
     * 照合表示状態
     */
    visibleCompare: function () {
      return this.viewState >= viewSts.READ_RULE;
    }
  },
  /**
   * ページ離脱前
   */
  async beforeRouteLeave(to, from, next) {
    await this.clearInput();
    next();
  },
  methods: {
    /**
     * 入力モード変更処理(親コンポーネントより)
     * @param {string} 入力モード(1:手入力、2:OCR、4:バーコード)
     */
    changeInputMode: function (mode) {
      this.selectedInputMode = mode;
    },
    /**
     * 入力クリア(親コンポーネントより)
     */
    clearInput: function () {
      if (this.$route.params['compareOrderNo']) {
        // 照合予定選択時は前画面へ戻る
        this.$router.back();
        this.$store.commit('clearHistoryCount');
      } else {
        // 読取ルールを再選択する
        this.onChangeReadId(this.item.readId);
      }
    },
    /**
     * スキャンデータ解析後事前処理
     * @param {number} viewState 画面状態
     * @param {object} headerValue ヘッダ値
     * @param {array} detailValues 明細値配列
     * @return {number} 画面状態、-1:解析エラー
     */
    analyzedBefore: async function (viewState, headerValue, detailValues) {
      // スキャンデータが解析できなかった場合はエラー
      if (Object.keys(headerValue).length === 0 && detailValues.length === 0) {
        if (viewState === viewSts.RESULT) {
          // 空データをセットし照合させる
          detailValues.push({});
          return viewSts.READ_RULE;
        } else {
          // 読取失敗で完了する
          this.$store.commit('setMessage', 'スキャンデータ解析エラー');
          return viewSts.ERROR;
        }
      }
      return viewState;
    },
    /**
     * スキャンデータ解析後Step1
     * @param {object} headerValue ヘッダ値
     * @param {array} detailValues 明細値配列
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {number} dupType 重複区分(1:重複OK、2:重複NG)
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep1: async function (headerValue, detailValues, barcode, nextBarcode, dupType, item) {
      // 初期値セット
      const read = this.$store.getters.currentRead;
      item.compareOrderNo = 0; // 照合予定NO
      item.compareResultNo = 0; // 照合予定NO
      item.orderCount = 0; // 予定件数
      item.resultCount = 0; // 実績件数
      item.details.splice(0); // 明細

      // ヘッダ情報セット
      let compareNo = compare.detials[0].compareNo;
      const compare = this.$store.getters.currentCompare;
      compareNo = this.readOrderHeaderValue(barcode, compare, compareNo, headerValue, item);
      if (compareNo === -1) {
        this.$store.commit('setMessage', '初回照合予定ヘッダ読取エラー');
        return viewSts.ERROR;
      }

      const detailCompares = compare.details.filter(
        (detail) => detail.dataLayout === dataLayout.DETAIL
      );

      if (detailValues.length > 0) {
        // 明細情報セット
        compareNo = detailCompares[0].compareNo;
        for (let i = 0; i < detailValues.length; i++) {
          compareNo = this.readOrderDetailValue(
            barcode,
            compare,
            detailCompares[0].compareNo,
            i,
            detailValues[i],
            item
          );
          if (this.nowCompareNo === -1) {
            this.$store.commit('setMessage', '初回照合予定明細読取エラー');
            return viewSts.ERROR;
          }
        }

        this.$set(item, 'orderCount', item.details.length);
        this.nowCompareNo = detailCompares[0].compareNo;

        this.$store.commit('setMessage', '照合先を読取します');
        return viewSts.RESULT; // 実績読取中
      } else {
        this.nowCompareNo = detailCompares[0].compareNo;
        this.$store.commit('setMessage', '照合予定明細を読取します');
        return viewSts.ORDER_DETAIL; // 予定明細読取中
      }
    },
    /**
     * スキャンデータ解析後Step2
     * @param {object} headerValue ヘッダ値
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep2: async function (headerValue, barcode, nextBarcode, item) {
      // ヘッダ読取
      const compare = this.$store.getters.currentCompare;
      this.nowCompareNo = this.readOrderHeaderValue(
        barcode,
        compare,
        this.nowCompareNo,
        headerValue,
        item
      );
      if (this.nowCompareNo === -1) {
        this.$store.commit('setMessage', '照合予定ヘッダ読取エラー');
        return viewSts.ERROR;
      }

      this.$store.commit('setMessage', '照合予定明細を読取します');
      const detailCompares = compare.details.filter(
        (detail) => detail.dataLayout === dataLayout.DETAIL
      );
      this.nowCompareNo = detailCompares[0].compareNo;
      return viewSts.ORDER_DETAIL; // 予定明細読取中
    },
    /**
     * スキャンデータ解析後Step3
     * @param {array} detailValues 明細値配列
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {string} dupType 重複区分(1:重複OK、2:重複NG)
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep3: async function (detailValues, barcode, nextBarcode, dupType, item) {
      const compare = this.$store.getters.currentCompare;

      // 明細情報セット
      this.nowCompareNo = this.readOrderDetailValue(
        barcode,
        compare,
        this.nowCompareNo,
        this.detailIndex,
        detailValues[0],
        item
      );
      if (this.nowCompareNo === -1) {
        this.$store.commit('setMessage', '照合予定明細読取エラー');
        return -1;
      }

      this.$set(item, 'orderCount', item.orderCount + 1);
      const detailCompares = compare.details.filter(
        (detail) => detail.dataLayout === dataLayout.DETAIL
      );
      this.nowCompareNo = detailCompares[0].compareNo;
      this.$store.commit('setMessage', '照合先を読取します');
      return viewSts.RESULT; // 実績読取中
    },
    /**
     * スキャンデータ解析後Step4
     * @param {array} detailValues 明細値配列
     * @param {object} barcode バーコード情報
     * @param {object} nextBarcode 次のバーコード情報
     * @param {string} dupType 重複区分(1:重複OK、2:重複NG)
     * @param {object} item 照合予定実績データ
     * @return {number} 画面状態
     */
    analyzedStep4: async function (detailValues, barcode, nextBarcode, dupType, item) {
      // 照合1項目目で複数照合の場合は照合対象行を検索する
      const compare = this.$store.getters.currentCompare;

      // 照合初回は全明細を対象とする
      const detailIndexes = [];
      for (let i = 0; i < item.details.length; i++) detailIndexes.push(i);

      // 重複NGの場合は照合実績重複チェックを行う
      if (dupType === allowDup.DUP_NG) {
        const ret = await this.checkDuplicatedCompareResult(compare, item, detailValues[0]);

        if (!ret) {
          this.$store.commit('setMessage', '通信エラー(照合実績重複チェック)');
          return viewSts.ERROR;
        }

        if (ret.compareResultNo !== 0) {
          alert('照合実績が重複しました');
          this.$store.commit('setMessage', `実績を再読取します`);
          return viewSts.RESULT; // 実績読取中
        }
      }

      // 明細照合処理を実施する
      let detailIndex = 0;
      this.nowCompareNo = this.compareDetailValue(
        barcode,
        compare,
        this.nowCompareNo,
        item,
        detailIndexes,
        detailValues[0]
      );
      const isNg = this.nowCompareNo === -1;
      if (detailIndexes.length === 1) detailIndex = detailIndexes[0];

      // 照合実績書き込み
      const ret = await this.writeCompareResult(compare, item, detailIndex);
      if (!ret) {
        this.$store.commit('setMessage', '通信エラー(照合実績登録)');
        return viewSts.ERROR;
      }

      item.compareOrderNo = ret.compareOrderNo; // 照合予定NO
      item.compareResultNo = ret.compareResultNo; // 照合実績NO

      // NG発生で即完了させる
      if (isNg) {
        alert('照合NGです');
        if (this.$route.params['compareOrderNo']) {
          // 照合予定選択時は前画面へ戻る
          this.$router.back();
          this.$store.commit('clearHistoryCount');
        } else {
          this.$store.commit('setMessage', '照合NGです');
        }
        return viewSts.READ_RULE; // 読取ルール選択後
      }

      this.$set(item, 'resultCount', item.resultCount + 1);
      item.details.splice(detailIndex, 1);
      if (item.resultCount < item.orderCount) {
        // 実績件数が予定件数に満たない場合は次の実績の読取待機
        const detailCompares = compare.details.filter(
          (detail) => detail.dataLayout === dataLayout.DETAIL
        );
        this.nowCompareNo = detailCompares[0].compareNo;
        this.$store.commit('setMessage', '次の照合先を読取します');
        return viewSts.RESULT; // 実績読取中
      } else {
        // 照合予定情報更新(完了)API(NGなし完了)
        const ret = await putCompareOrderCompleteAPI(item.compareOrderNo, completeSts.COMPLETE_OK);
        if (!ret) {
          this.$store.commit('setMessage', '通信エラー(照合実績完了更新)');
          return viewSts.ERROR;
        }

        if (this.$route.params['compareOrderNo']) {
          // 照合予定選択時は前画面へ戻る
          this.$store.commit('clearHistoryCount');
          this.$router.back();
        } else {
          this.$store.commit('setMessage', '照合OKです');
        }
        return viewSts.READ_RULE; // 読取ルール選択後
      }
    },
    /**
     * 読取ルール変更時
     * @param {string} event イベント引数(読取ID)
     */
    onChangeReadId: function (event) {
      this.$store.commit('setMessage', null);

      // 読取IDを保持
      this.$store.commit('setReadId', event);

      // 画面情報の属性をセット
      const read = this.$store.getters.currentRead;
      if (!read) {
        this.item.readId = null;
        this.$emit('enabledScan', false);
        this.viewState = viewSts.INIT; // 初期表示
        return;
      }
      this.item = {
        compareOrderNo: 0, // 照合予定NO
        compareResultNo: 0, // 照合実績NO
        readId: read.readId, // 読取ID
        inputId: null, // 取込ID
        dataType: read.dataType, // データ区分
        orderCount: 0, // 予定件数
        resultCount: 0, // 実績件数
        details: [] // 明細
      };

      // ヘッダ項目セット
      const compare = this.$store.getters.currentCompare;
      if (compare) {
        this.headerFields = getCompareHeaderFields(compare);

        // 明細項目セット
        const fields = getCompareDetailFields(compare);
        this.detailFields = fields.map(function (x) {
          return { key: x.key, label: x.label, thClass: 'p-1', tdClass: 'px-1' };
        });
      } else {
        this.headerFields.splice(0);
        this.detailFields.splice(0);
      }

      this.viewState = viewSts.READ_RULE; // 読取ルール選択後
      this.$store.commit('setMessage', '照合予定を読取します');

      this.$emit('enabledScan', true);

      // 読取ルールのフォーカスを外す
      if (!this.$route.params['compareOrderNo']) this.$refs.selectReadId?.blur();
    },
    /**
     * 照合予定情報取得
     * @param {object} compare 照合情報
     * @param {number} compareOrderNo 照合予定ID
     */
    getCompareOrder: async function (compare, compareOrderNo) {
      // 照合実績情報取得API
      const ret = await getCompareOrderAPI(compareOrderNo);
      if (!ret) {
        this.viewState = viewSts.READ_RULE; // 読取ルール選択後
        this.$store.commit('setMessage', '通信エラー(照合予定取得)');
        return;
      }

      this.item = ret.data;
      convertItemFormat(compare, this.item);

      const detailCompares = compare.details.filter(
        (cdetail) => cdetail.dataLayout === dataLayout.DETAIL
      );
      this.nowCompareNo = detailCompares[0].compareNo;
      this.viewState = viewSts.RESULT; // 実績読取中
      this.$store.commit('setMessage', '照合先を読取します');
    }
  },
  mixins: [CompareValueMixin]
};
</script>

<style>
div.container-fluid {
  height: 100%;
}
.b-table-sticky-header {
  height: 100%;
  max-height: none !important;
  -webkit-flex: 1 1 0;
  -ms-flex: 1 1 0;
  flex: 1 1 0;
}
</style>
